import { useCallback, useEffect } from "react";

import { type SetterOrUpdater, useRecoilState, useResetRecoilState } from "recoil";

import { useBlizzardEvents } from "../../../analytics/hooks";
import { type MyLensesLensStatus, type SortBy } from "../../../gql-generated/types";
import { filterByStatusAtom, filterIsEmptyAtom, searchInputAtom } from "../../../lenses/state";
import { PaginationAtomsKeys } from "../../../shared/state";
import { type Pagination, type PaginationFilter, defaultPaginationFilter } from "../models";
import { paginationAtom, paginationFilterAtom, updateSort } from "../state";

export const usePagination = (
    filterByInitialOptions?: Partial<PaginationFilter>,
    paginationAtomsKey: PaginationAtomsKeys = PaginationAtomsKeys.LensesList,
): {
    pagination: Pagination;
    setPagination: SetterOrUpdater<Pagination>;
    filterBy: PaginationFilter;
    updateFilterBy: (nextFilterByStatus: MyLensesLensStatus[]) => void;
    setSearch: (search: string) => void;
    onSortChange: (sortBy: SortBy) => void;
    resetSettings: () => void;
} => {
    const [pagination, setPagination] = useRecoilState(paginationAtom(paginationAtomsKey));
    const [filterBy, setFilterBy] = useRecoilState(paginationFilterAtom(paginationAtomsKey));

    // Reset Pagination
    const resetPagination = useResetRecoilState(paginationAtom(paginationAtomsKey));
    const resetFilterBy = useResetRecoilState(paginationFilterAtom(paginationAtomsKey));

    // Pagination on the UI
    const resetSearch = useResetRecoilState(searchInputAtom(paginationAtomsKey));
    const resetFilterByStatus = useResetRecoilState(filterByStatusAtom(paginationAtomsKey));
    const resetFilterIsEmpty = useResetRecoilState(filterIsEmptyAtom(paginationAtomsKey));

    const { sendBlizzardEvent } = useBlizzardEvents();

    const setSearch = useCallback(
        (searchName: string = "") => {
            if (filterBy.searchName === searchName) {
                return;
            }
            if (searchName === "" || (searchName && searchName.length >= 3)) {
                const nextFilterBy = { ...filterBy, searchName };
                setFilterBy(nextFilterBy);

                if (searchName.length > 0) {
                    sendBlizzardEvent({
                        event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
                        pageType: "LENS_TABLE",
                        action: "SEARCH_LENSES",
                    });
                }
            }
        },
        [setFilterBy, filterBy, sendBlizzardEvent],
    );

    // set initial options
    useEffect(() => {
        if (filterByInitialOptions) {
            setFilterBy({
                ...defaultPaginationFilter,
                ...filterByInitialOptions,
            });
        }
    }, [setFilterBy, filterByInitialOptions]);

    const onSortChange = useCallback(
        (sortBy: SortBy) => {
            const nextPagination = updateSort(pagination, sortBy);
            setPagination(nextPagination);
        },
        [setPagination, pagination],
    );

    const updateFilterBy = useCallback(
        (nextFilterByStatus: MyLensesLensStatus[]) => {
            const nextFilterBy = { ...filterBy, statuses: nextFilterByStatus };
            setFilterBy(nextFilterBy);
        },
        [setFilterBy, filterBy],
    );

    // TODO: we should be able to remove this with the use of the atom families
    const resetSettings = useCallback(() => {
        resetPagination();
        resetFilterBy();
        resetSearch();
        resetFilterByStatus();
        resetFilterIsEmpty();
    }, [resetPagination, resetFilterBy, resetSearch, resetFilterByStatus, resetFilterIsEmpty]);

    return {
        pagination,
        setPagination,
        filterBy,
        updateFilterBy,
        setSearch,
        onSortChange,
        resetSettings,
    };
};
